import { Component, NgZone } from "@angular/core";
import { combineLatest } from "rxjs";
import { filter, map, shareReplay, take } from "rxjs/operators";
import { RequestInvitationFormData } from "../../../modals/request-invitation/request-invitation.model";
import { Client, UtilsService } from "projects/common/src/public-api";
import { ModalBehavior, ModalsService } from "projects/common/src/lib/services/modals.service";
import { JobRestore, JobsService, JobUpdate, JobUserRestore } from "../../../services/jobs.service";
import { BusinessService } from "../../../services/business.service";
import { ActivatedRoute, Router } from "@angular/router";
import { UsersService } from "../../../services/users.service";
import { MatLegacySnackBar } from "@angular/material/legacy-snack-bar";
import { NotesService } from "../../../services/notes.service";
import { ProgressBarService } from "../../../services/progress-bar.service";
import { EstimatesService } from "../../../services/estimates.service";
import { InvoicesService } from "../../../services/invoices.service";
import { ScrollDispatcher } from "@angular/cdk/scrolling";
import { ScheduleService } from "../../../services/schedule.service";
import { ChatService } from "../../../services/chat.service";
import { ViewAsService } from "../../../services/view-as.service";
import { NavigationService } from "../../../services/navigation.service";
import { Job } from "../../../models/jobs.model";
import { clientRestoreFromDocument } from "projects/common/src/lib/models/client-transform";
import { JobRequestInvitationFormData } from "../../../modals/assigned-dialog/assigned-dialog.component";
import { sameRanges } from "projects/common/src/lib/models/time-range.model";
import { ConfirmationDialog } from "projects/common/src/lib/modals/confirmation-dialog/confirmation-dialog.component";
import { showSnackbar } from "projects/common/src/lib/components/snackbar/snackbar.component";
import { AssessmentOrJobComponent } from "./assessment-or-job.compenent";
import { ProgressBarStage } from "../../../models/progress-bar.model";

@Component({
  selector: 'app-job-info',
  templateUrl: './assessment-or-job.component.html',
  styleUrls: ['../event-info.component.scss']
})
export class JobComponent extends AssessmentOrJobComponent<Job> {
  submitStates = ['Submitted', 'On Hold', 'Canceled'];
  
  ownerOrLastAssignedBy$ = combineLatest([this.document$, this.usersService.users$]).pipe(
    map(([document, users]) => {
      if(!document)
        throw new Error('Shouldn\'t be able to reach this since we check for nullability in this.document$');
      let lastUser = document.users[0];
      for(const user of document.users) {
        if(user.assignedAt.getTime() > lastUser.assignedAt.getTime())
          lastUser = user;
      }
      return users!.find(user => user.id === lastUser.assignedBy)!;
    }),
    shareReplay({bufferSize: 1, refCount: true})
  );
  
  assignees$ = combineLatest([
    this.currentUser$,
    this.usersService.users$,
    this.document$,
    this.ownerOrLastAssignedBy$
  ]).pipe(
    filter(([currentUser, users, document, ownerOrLastAssignedBy]) => !!users && !!document),
    map(([currentUser, users, document, ownerOrLastAssignedBy]) => {
      if (!users || !document)
        return [];
      const assignees = document.users
        .map(jobUser => {
          const user = users.find((user) => user.id === jobUser.userId)!;
          return {user, acceptanceStatus: jobUser.acceptanceStatus}
        })
        .sort((a, b) => a.user.id === currentUser.id ? -1 : 1);
      const assignedByIndex = assignees.findIndex(a => a.user.id === ownerOrLastAssignedBy.id);
      if(assignedByIndex !== -1 && assignees[assignedByIndex].acceptanceStatus === 'looped_in')
        assignees.splice(assignedByIndex, 1);
      assignees.sort((a, b) => {
        if(a.acceptanceStatus === 'looped_in')
          return -1;
        if(b.acceptanceStatus === 'looped_in')
          return 1;
        return 0;
      });
      return assignees;
    }),
    shareReplay({bufferSize: 1, refCount: true})
  );

  constructor(
    private jobsService: JobsService,
    modalsService: ModalsService,
    businessService: BusinessService,
    router: Router,
    route: ActivatedRoute,
    usersService: UsersService,
    snackbar: MatLegacySnackBar,
    notesService: NotesService,
    progressBarService: ProgressBarService,
    estimatesService: EstimatesService,
    invoicesService: InvoicesService,
    scrollDispatcher: ScrollDispatcher,
    viewAsService: ViewAsService,
    scheduleService: ScheduleService,
    chatService: ChatService,
    utilsService: UtilsService,
    navigationService: NavigationService,
    zone: NgZone,
  ) {
    super(
      progressBarService,
      estimatesService,
      invoicesService,
      chatService,
      snackbar,
      scheduleService,
      modalsService,
      businessService,
      router,
      route,
      usersService,
      notesService,
      scrollDispatcher,
      viewAsService,
      utilsService,
      navigationService,
      zone,
    );
    this.docSubject.next(this.jobsService.jobObservable(this.documentId, 'Job'));
  }

  getAssignees(document: Job): number[] {
    return (document.users.length > 1 ? document.users.filter(u => u.acceptanceStatus !== 'looped_in') : document.users).map(u => u.userId);
  }

  getSelectedStatusIndex(document: Job) {
    let jobStatus = document.status.toLowerCase();
    if(jobStatus === 'pending')
      jobStatus = 'on hold';
    return this.submitStates.findIndex(status => status.toLowerCase() === jobStatus);
  }
  
  isLoopedIn(userId: number) {
    return this.document$.pipe(
      map(document => {
        if(!document)
          return false;
        return !!document.users.find(user => user.userId === userId && user.acceptanceStatus === 'looped_in');
      })
    );
  }

  async deleteDocument(id: number, workflowId: number) {
    const url = this.router.url;
    const router = this.router;
    const notes = await this.notesService.notesObservable(workflowId).pipe(take(1)).toPromise();
    const job = await this.jobsService.getJob(id);
    const restoreData: JobRestore = {
      inId: job.id,
      inCreatedAt: job.createdAt,
      inCreatedBy: job.createdBy,
      inClient: await clientRestoreFromDocument(job),
      inRanges: job.ranges,
      inStatus: job.status,
      inJobUsers: job.users.map(user => ({
        id: user.id,
        assigneeUserId: user.userId,
        assignedByUserId: user.assignedBy,
        acceptanceStatus: user.acceptanceStatus
      }) as JobUserRestore),
      inWorkflowId: job.workflowId,
      inJobType: job.jobType,
      inNotes: notes!
    };

    if(job.statusStoreForCancel)
      restoreData.inStatusStoreForCancel = job.statusStoreForCancel;

    this.modalsService.open(ConfirmationDialog, {
      behavior: ModalBehavior.Dialog,
      disableClose: true,
      data: {
        title: "Delete",
        message: "Are you sure you want to delete this job?",
        actionTitle: "Delete",
        actionColor: 'warn',
        action: async () => {
          await this.jobsService.deleteJob(id);
          await this.close();
          showSnackbar(this.snackbar, {
            message: 'Job deleted',
            duration: 10000,
            actionText: 'Undo',
            action: async () => {
              await this.jobsService.restoreJob(restoreData);
              router.navigateByUrl(url);
            }
          });
        }
      }
    });
  }

  async openAssessment(document: Job, assessmentStage: ProgressBarStage) {
    const assessmentId = assessmentStage.id;
    const workflowId = document.workflowId;
    if(assessmentStage.canOpen)
      await this.router.navigate(['/assessments', workflowId, assessmentId]);
  }

  async resendClick() {
    const document = (await this.document$.pipe(take(1)).toPromise())!;

    // Check if client has an email address
    if (!document.email || document.email.trim() === '') {
      showSnackbar(this.snackbar, {
        message: 'Cannot send job: client email is required',
        duration: 3000
      });
      return;
    }

    this.jobsService.sendEmailForJob(document, false);
    showSnackbar(this.snackbar, {
      message: 'Sent by email'
    });
  }

  async submitStateChanged(value: string, document: Job) {
    if (value === 'On Hold')
      value = 'pending';

    await this.jobsService.updateJob({
      inId: document.id,
      inStatus: value.toLowerCase() as Job['status']
    });
  }

  async onRequestInvitationSubmit(original: Job, data: Required<RequestInvitationFormData>, create: boolean) {
    if(create) {
      await this.scheduleService.createDocument(data, original.workflowId);
      return null;
    }
    const currentUser = await this.currentUser$.pipe(take(1)).toPromise();
    const reassigned = (data as JobRequestInvitationFormData).unassignedUserIds.filter(id => !!data.assignees.find(u => u.id === id));
    const updateData: JobUpdate = {
      inId: original.id,
      inClient: data.client as Client,
      inJobType: data.jobType,
      inRanges: data.ranges,
      inAssignedBy: currentUser.id,
      inReassignedUserIds: reassigned
    };
    if(data.assignees) {
      updateData.inJobUserIds = data.assignees
          .filter(user => !(data as JobRequestInvitationFormData).unassignedUserIds.includes(user.id))
          .map(user => user.id);
    }
    await this.jobsService.updateJob(updateData);
    const clientEmailChange = original.email !== data.client.email;
    const rangesChanged = !sameRanges(original.ranges, data.ranges);
    if(clientEmailChange || rangesChanged) {
      // Check if client has an email address before sending
      if (!data.client.email || data.client.email.trim() === '') {
        showSnackbar(this.snackbar, {
          message: 'Cannot send job: client email is required',
          duration: 3000
        });
      } else {
        await this.jobsService.sendEmailForFormData(data, original, !clientEmailChange);
      }
    }
    return null;
  }

}